import { createI18n } from "vue-i18n";

import fr_app from "./fr/app.json";
import fr_badges from "./fr/badges.json";
import fr_game from "./fr/game.json";
import fr_news from "./fr/news.json";
import fr_other from "./fr/other.json";
import fr_skills from "./fr/skills.json";

import en_app from "./en/app.json";
import en_badges from "./en/badges.json";
import en_game from "./en/game.json";
import en_news from "./en/news.json";
import en_other from "./en/other.json";
import en_skills from "./en/skills.json";

// Local fallback messages
const localMessages = {
  fr: {
    app: fr_app,
    badges: fr_badges,
    game: fr_game,
    news: fr_news,
    other: fr_other,
    skills: fr_skills,
  },
  en: {
    app: en_app,
    badges: en_badges,
    game: en_game,
    news: en_news,
    other: en_other,
    skills: en_skills
  }
};

// Get the i18n base URL from environment variables
const I18N_BASE_URL = import.meta.env.VITE_I18N_BASE_URL || 'https://dyljnx6ncc5ku.cloudfront.net';

// Available locales and message types
const SUPPORTED_LOCALES = ['fr', 'en'];
const MESSAGE_TYPES = ['app', 'badges', 'game', 'news', 'other', 'pdf', 'skills'];

/**
 * Load a remote message file with fallback to local
 * @param {string} locale - The locale (e.g., 'fr', 'en')
 * @param {string} type - The message type (e.g., 'app', 'badges')
 * @returns {Promise<Object>} The loaded messages
 */
async function loadRemoteMessages(locale, type) {
  try {
    const url = `${I18N_BASE_URL}/${locale}/${type}.json`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // Add timeout for better error handling
      signal: AbortSignal.timeout(10000) // 10 second timeout
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const messages = await response.json();
    return messages;
  } catch (error) {
    let errorType = 'Network Error';

    // Detect specific error types
    if (error.name === 'AbortError') {
      errorType = 'Timeout';
    } else if (error.message.includes('CORS')) {
      errorType = 'CORS Error';
      console.warn(`🚫 CORS Error for ${locale}/${type}.json - CloudFront needs CORS configuration`);
    } else if (error.message.includes('Failed to fetch')) {
      errorType = 'Network/CORS Error';
      console.warn(`🚫 Network/CORS Error for ${locale}/${type}.json - Check CloudFront CORS settings`);
    }

    console.warn(`⚠️ ${errorType}: Failed to load remote ${locale}/${type}.json, using local fallback:`, error.message);
    return localMessages[locale]?.[type] || {};
  }
}

/**
 * Load all message types for a specific locale
 * @param {string} locale - The locale to load
 * @returns {Promise<Object>} Complete messages object for the locale
 */
async function loadLocaleMessages(locale) {
  const messages = {};

  // Load all message types in parallel
  const loadPromises = MESSAGE_TYPES.map(async (type) => {
    const remoteMessages = await loadRemoteMessages(locale, type);
    // Merge remote messages with local fallback
    messages[type] = { ...localMessages[locale]?.[type], ...remoteMessages };
  });

  await Promise.all(loadPromises);
  return messages;
}

/**
 * Initialize i18n with remote messages and local fallbacks
 */
async function initializeI18n() {
  // Start with local messages as base
  const messages = { ...localMessages };

  try {
    // Load remote messages for all supported locales
    const remoteLoadPromises = SUPPORTED_LOCALES.map(async (locale) => {
      const remoteMessages = await loadLocaleMessages(locale);
      messages[locale] = remoteMessages;
    });

    await Promise.all(remoteLoadPromises);
  } catch (error) {
    console.error('Error loading remote messages, using local fallbacks:', error);
  }

  return messages;
}

// Initialize the i18n instance
export const i18n = createI18n({
  legacy: false, // Recommended for Composition API
  locale: localStorage.getItem("yuzuLocale") || navigator.language.split("-")[0] || "fr",
  fallbackLocale: "en", // Fallback locale
  messages: localMessages, // Start with local messages
  globalInjection: true, // To use $t in templates without importing useI18n
});

// Load remote messages and update i18n instance
initializeI18n().then((messages) => {
  // Update messages in the i18n instance
  SUPPORTED_LOCALES.forEach(locale => {
    if (messages[locale]) {
      i18n.global.mergeLocaleMessage(locale, messages[locale]);
    }
  });
});

/**
 * Function to change the locale and persist it
 * @param {string} newLocale - The new locale to set
 */
export function setLocale(newLocale) {
  if (i18n.global.availableLocales.includes(newLocale)) {
    i18n.global.locale.value = newLocale;
    localStorage.setItem("yuzuLocale", newLocale);
  } else {
    console.warn(`Locale ${newLocale} is not available.`);
  }
}

/**
 * Manually reload messages for a specific locale from remote source
 * @param {string} locale - The locale to reload
 */
export async function reloadLocaleMessages(locale) {
  if (!SUPPORTED_LOCALES.includes(locale)) {
    console.warn(`Locale ${locale} is not supported.`);
    return;
  }

  try {
    console.log(`Reloading messages for locale: ${locale}`);
    const messages = await loadLocaleMessages(locale);
    i18n.global.setLocaleMessage(locale, messages);
    console.log(`Successfully reloaded messages for locale: ${locale}`);
  } catch (error) {
    console.error(`Failed to reload messages for locale ${locale}:`, error);
  }
}

export default i18n;
